// IndexedDB persistence for chat state. Three object stores in one DB:
//
//   chats     keyed by an owner-composite id (`${tenantId}:${userId}:${localId}`).
//             Holds chat metadata, visible items, agent.state.messages
//             snapshot, canvas state, and small
//             (≤PREVIEW_ROW_THRESHOLD-row) tool result entries.
//             Written on every state change, debounced. A `byTenant`
//             index scopes reads so one tenant never sees another's
//             chats when the same browser has signed into both.
//
//   chatMeta  keyed by the same tenant-composite id. Holds ONLY the
//             handful of fields the tab strip + cold-load landing need
//             (kind, title, hasItems, busyAtClose, savedAt,
//             schemaVersion). Written in the same
//             transaction as the matching `chats` body so the two can
//             never drift. Exists because IndexedDB has no way to read
//             a sub-field of a record — `getAll` deserializes the whole
//             stored object — so rendering the tab list out of the
//             `chats` store would force a structured-clone of every
//             chat's full transcript + LLM history just to read titles.
//             Reading the tiny `chatMeta` records instead keeps mount
//             cheap no matter how many large chats exist; bodies are
//             loaded lazily (loadChat) on first activation. See O-1746.
//
//   largeRows keyed by `${tenantId}:${localId}:${toolCallId}`, indexed
//             by `chatId` (the `${tenantId}:${localId}` composite) and
//             by `tenantId`. Holds the full row payload for
//             analytics_query / execute_python results beyond the
//             inline-preview cutoff.
//
// Why composite keys, not a plain owner filter: `localId` is a
// per-browser integer counter (1, 2, 3…) recomputed from the visible
// set, so tenant B's "chat 1" would silently overwrite tenant A's
// "chat 1" if they shared the bare integer key. The tenant prefix
// keeps each tenant's keyspace disjoint.
//
// All operations fail soft: if indexedDB is unavailable (private
// browsing, Safari quirks, quota exceeded), the wrapper resolves with
// a benign empty/null/false and logs once. The caller never has to
// branch on storage availability — persistence is best-effort.

// Renamed from the pre-cleanup codename DB during the codename cleanup.
// No migration: a fresh DB name starts empty, so chat history created
// under the old name is intentionally not carried over (it resets).
import { resolveFenceAction } from "./chatIds.js";
import { currentBrowserStorageOwner } from "./browserStorageScope.js";

const DB_NAME = "origin-chat";
const SCOPED_DB_PREFIX = "origin-chat-v5";
const DB_VERSION = 4;

// Set once the one-time legacy purge completes so we don't re-scan
// every mount — see purgeLegacyChats.
const PURGED_FLAG = "origin-chat-legacy-purged";

const _dbPromises = new Map();
let _unavailable = false;

// One-way kill switch for writes. Set by LogoutDialog before the
// tenant-wide wipe runs: ChatConsole's window.beforeunload /
// visibilitychange handlers would otherwise call flushPendingSaves
// during the WorkOS redirect and write the in-memory transcript
// right back into IDB. Once suppressed, saveChat / saveLargeRows
// no-op for the rest of the page lifetime; the page is about to
// navigate away to WorkOS, so there is nothing legitimate left to
// persist. NOT set for the Settings clear flow - that one wants the
// user to keep chatting after the clear, and the notify-driven
// in-memory reset below removes the race without the kill switch.
let _writesSuppressed = false;
export function suppressFurtherWrites() {
    _writesSuppressed = true;
}

// Subscriber set for "chats were wiped externally". Consumers
// (mainly ChatConsole) listen and reset their in-memory chat state
// so the post-wipe flushPendingSaves has nothing populated to
// re-persist. Mirrors workflowStore's subscribeWorkflows pattern.
const _clearListeners = new Set();
export function subscribeChatsCleared(fn) {
    _clearListeners.add(fn);
    return () => _clearListeners.delete(fn);
}
function notifyCleared() {
    for (const fn of _clearListeners) {
        try { fn(); } catch { /* one listener must not block siblings */ }
    }
}

function tenantKey(tenantId) {
    return currentBrowserStorageOwner(tenantId)?.key ?? null;
}

// Composite primary key for the `chats` store and the `chatId` field
// on `largeRows`. Tenant prefix keeps per-tenant integer ids disjoint.
function chatKey(tenantId, chatId) {
    const ownerKey = tenantKey(tenantId);
    return ownerKey == null ? null : chatKeyForOwner(ownerKey, chatId);
}

function chatKeyForOwner(ownerKey, chatId) {
    return `${ownerKey}:${String(chatId)}`;
}

function getDb(tenantId) {
    if (_unavailable) return Promise.resolve(null);
    const owner = currentBrowserStorageOwner(tenantId);
    if (!owner) return Promise.resolve(null);
    if (_dbPromises.has(owner.key)) return _dbPromises.get(owner.key);
    if (typeof indexedDB === "undefined") {
        _unavailable = true;
        console.warn("[chatStorage] indexedDB unavailable; chat persistence disabled.");
        return Promise.resolve(null);
    }
    const dbPromise = new Promise((resolve) => {
        let opened = false;
        const req = indexedDB.open(`${SCOPED_DB_PREFIX}:${owner.key}`, DB_VERSION);
        req.onupgradeneeded = (event) => {
            const db = req.result;
            const tx = req.transaction; // the version-change transaction
            if (event.oldVersion < 1) {
                if (!db.objectStoreNames.contains("chats")) {
                    db.createObjectStore("chats", { keyPath: "chatId" });
                }
                if (!db.objectStoreNames.contains("largeRows")) {
                    const rows = db.createObjectStore("largeRows", { keyPath: "key" });
                    rows.createIndex("chatId", "chatId", { unique: false });
                }
            }
            if (event.oldVersion < 2) {
                // Add tenant indexes to both stores. Records written
                // before v2 carry no `tenantId`, so they're invisible to
                // these indexes; purgeLegacyChats deletes them outright.
                const chats = tx.objectStore("chats");
                if (!chats.indexNames.contains("byTenant")) {
                    chats.createIndex("byTenant", "tenantId", { unique: false });
                }
                const rows = tx.objectStore("largeRows");
                if (!rows.indexNames.contains("byTenant")) {
                    rows.createIndex("byTenant", "tenantId", { unique: false });
                }
            }
            if (event.oldVersion < 3) {
                // Add the lightweight `chatMeta` store. Just create it
                // empty here — existing `chats` bodies are projected into
                // it lazily by the app's first load (loadAllChatMeta +
                // backfillChatMeta), which keeps this upgrade cheap and
                // fail-soft rather than reading every large body inside
                // the blocking version-change transaction.
                if (!db.objectStoreNames.contains("chatMeta")) {
                    const meta = db.createObjectStore("chatMeta", { keyPath: "chatId" });
                    meta.createIndex("byTenant", "tenantId", { unique: false });
                }
            }
            if (event.oldVersion < 4) {
                // Pending-sync outbox: one row per chat holding the
                // server-acked revision + dirty/terminal state that drives
                // the boundary sync layer. Server-side truth lives in PG;
                // this store is only the local record of what has/hasn't
                // been pushed, so it starts empty and self-heals on the
                // next sync boundary.
                if (!db.objectStoreNames.contains("chatSync")) {
                    const sync = db.createObjectStore("chatSync", { keyPath: "chatId" });
                    sync.createIndex("byTenant", "tenantId", { unique: false });
                }
            }
        };
        req.onsuccess = () => {
            opened = true;
            const db = req.result;
            // Step aside when another tab needs to upgrade the DB (e.g.
            // a deploy bumps DB_VERSION and a second tab loads the new
            // build). Without this, the upgrading tab's open() fires
            // `onblocked` and hangs — and so does every getDb() awaiting
            // it — which surfaces as "chat doesn't load at all" whenever
            // a second tab is open across a version bump. Closing here
            // lets the other tab's version-change transaction proceed;
            // we drop the cached handle so our next call re-opens at the
            // new version. (O-1746: first bump to expose this path.)
            db.onversionchange = () => {
                try { db.close(); } catch { /* already closing */ }
                _dbPromises.delete(owner.key);
            };
            resolve(db);
        };
        req.onerror = () => {
            if (opened) return;
            console.warn("[chatStorage] failed to open DB:", req.error);
            _unavailable = true;
            resolve(null);
        };
        req.onblocked = () => {
            // Our upgrade is blocked by an OLDER connection that hasn't
            // closed (typically a background tab on a previous build that
            // predates the onversionchange handler above). Give it a
            // grace period to step aside, then fail soft to
            // no-persistence rather than hanging the chat forever — and
            // clear the cache so a later mount retries once the other
            // connection is gone.
            console.warn("[chatStorage] DB open blocked by another connection; waiting for it to close.");
            setTimeout(() => {
                if (opened) return;
                console.warn("[chatStorage] DB still blocked; disabling chat persistence for this session.");
                _dbPromises.delete(owner.key);
                resolve(null);
            }, 3000);
        };
    });
    _dbPromises.set(owner.key, dbPromise);
    return dbPromise;
}

async function getDbContext(tenantId) {
    const owner = currentBrowserStorageOwner(tenantId);
    if (!owner) return null;
    const db = await getDb(owner.tenantId);
    return db ? { db, owner } : null;
}

// The one parser for "when": local writers stamp epoch ms, the server
// sends an ISO string, and legacy/imported records can carry either (or
// a numeric string). Every timestamp crossing the projection boundary
// goes through here so the meta store always holds epoch ms or null —
// mixing the two representations is how a fresh chat once rendered
// "19h ago". Exported for unit testing and for chatRestore's use.
export function toEpochMs(value) {
    if (value == null || value === "") return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value !== "string") return null;
    if (/^-?\d+(\.\d+)?$/.test(value.trim())) {
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
    }
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
}

// Project the tab-strip fields out of a full chat payload — the only
// thing the cheap on-mount read (loadAllChatMeta) needs to render the
// tab list. Kept next to saveChat so the body and its projection never
// disagree about what "metadata" is. Exported for unit testing.
export function projectChatMeta(tenantId, chatId, payload) {
    const ownerKey = tenantKey(tenantId);
    return projectChatMetaForOwner(ownerKey, chatId, payload);
}

function projectChatMetaForOwner(ownerKey, chatId, payload) {
    return {
        chatId: ownerKey == null ? null : chatKeyForOwner(ownerKey, chatId),
        tenantId: ownerKey,
        localId: String(chatId),
        schemaVersion: payload.schemaVersion,
        // Chat "kind" (null for a normal chat, "scout" for a tenant-profile
        // run). Carried in the lightweight index so cold-load can land on
        // the scout tab without reading every body. See ChatConsole's
        // landing policy.
        kind: payload.kind ?? null,
        title: payload.title || "New chat",
        // "Has content", not "has a local body": a restored stub legitimately
        // holds turns on the server while its items[] is still empty, and
        // consumers use this to tell a real chat from a blank draft.
        hasItems:
            (Array.isArray(payload.items) && payload.items.length > 0)
            || (payload.serverTurnCount ?? 0) > 0,
        busyAtClose: !!payload.busyAtClose,
        savedAt: toEpochMs(payload.savedAt),
        // Server chat UUID (chat persistence). Carried in the lightweight
        // index so the sync layer can map a local chat onto its server row
        // without deserializing the full body. Null for chats created before
        // persistence shipped, until their first sync/backfill mints one.
        serverChatId: payload.serverChatId ?? null,
    };
}

// Persist a chat payload under the tenant-composite key. The payload's
// own `chatId` field (the local id from the serializer) is preserved
// as `localId` so hydrate can recover the React-side id; the stored
// `chatId` keyPath becomes the composite primary key.
//
// Writes the full body to `chats` AND its tab-strip projection to
// `chatMeta` in ONE transaction, so the cheap metadata read on mount
// can never see a title/body that disagree (and a mid-write failure
// leaves neither store updated).
export async function saveChat(tenantId, chatId, payload) {
    if (_writesSuppressed) return false;
    const context = await getDbContext(tenantId);
    if (!context) return false;
    const { db, owner } = context;
    const key = chatKeyForOwner(owner.key, chatId);
    return new Promise((resolve) => {
        try {
            const tx = db.transaction(["chats", "chatMeta"], "readwrite");
            tx.objectStore("chats").put({
                ...payload,
                chatId: key,
                tenantId: owner.key,
                localId: String(chatId),
            });
            tx.objectStore("chatMeta").put(projectChatMetaForOwner(owner.key, chatId, payload));
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => {
                console.warn("[chatStorage] saveChat failed:", tx.error);
                resolve(false);
            };
            tx.onabort = () => {
                console.warn("[chatStorage] saveChat aborted:", tx.error);
                resolve(false);
            };
        } catch (err) {
            console.warn("[chatStorage] saveChat threw:", err);
            resolve(false);
        }
    });
}

// Load one chat's full body. Resolves the record, or `null` for a
// genuinely ABSENT record (and when storage is unavailable, which is
// equally non-destructive since saveChat then no-ops too). REJECTS on a
// real read failure (transaction abort, quota, disk error) so callers
// can tell "this chat has no body" from "we couldn't read it" — the
// lazy-hydrate path relies on that distinction to avoid marking a chat
// loaded-but-empty and then clobbering its still-intact on-disk
// transcript. See loadChatBodyIntoState (O-1746).
export async function loadChat(tenantId, chatId) {
    const context = await getDbContext(tenantId);
    if (!context) return null;
    const { db, owner } = context;
    const key = chatKeyForOwner(owner.key, chatId);
    return new Promise((resolve, reject) => {
        try {
            const tx = db.transaction("chats", "readonly");
            const req = tx.objectStore("chats").get(key);
            req.onsuccess = () => resolve(remapForHydrate(req.result) || null);
            req.onerror = () => {
                console.warn("[chatStorage] loadChat failed:", req.error);
                reject(req.error || new Error("loadChat read failed"));
            };
        } catch (err) {
            console.warn("[chatStorage] loadChat threw:", err);
            reject(err);
        }
    });
}

// Returns every persisted chat record for one tenant, in insertion
// order. The caller is responsible for filtering bad/unsupported
// schemaVersion values. `chatId` is remapped back to the local id so
// the hydrator (which reads `payload.chatId` as the React id) is
// unaffected by the composite-key storage scheme.
export async function loadAllChats(tenantId) {
    const context = await getDbContext(tenantId);
    if (!context) return [];
    const { db, owner } = context;
    return new Promise((resolve) => {
        try {
            const tx = db.transaction("chats", "readonly");
            const idx = tx.objectStore("chats").index("byTenant");
            const req = idx.getAll(IDBKeyRange.only(owner.key));
            req.onsuccess = () => {
                const rows = Array.isArray(req.result) ? req.result : [];
                resolve(rows.map(remapForHydrate));
            };
            req.onerror = () => {
                console.warn("[chatStorage] loadAllChats failed:", req.error);
                resolve([]);
            };
        } catch (err) {
            console.warn("[chatStorage] loadAllChats threw:", err);
            resolve([]);
        }
    });
}

// Maintain the tenant's ID FENCE — a chatMeta-only row, deliberately
// pinned at schemaVersion 1, whose numeric local id sits above every
// real chat id (see chatIds.js for the full rationale: already-deployed
// builds filter metas to schemaVersion === 1 before computing their
// next-chat-id counter, so without a v1-visible ceiling row a stale
// bundle would mint ids that collide with — and saveChat-overwrite —
// records migrated to newer schema versions). No body record is
// written; the row exists purely to be counted. No-ops when an
// existing fence already covers `targetFenceId`.
export async function ensureIdFence(tenantId, targetFenceId) {
    if (_writesSuppressed) return false;
    const target = Math.floor(Number(targetFenceId));
    if (!Number.isFinite(target) || target <= 0) return false;
    const metas = await loadAllChatMeta(tenantId);
    const { put, deleteLocalIds } = resolveFenceAction(metas, target);
    if (put == null) return true;
    const context = await getDbContext(tenantId);
    if (!context) return false;
    const { db, owner } = context;
    return new Promise((resolve) => {
        try {
            const tx = db.transaction("chatMeta", "readwrite");
            const store = tx.objectStore("chatMeta");
            for (const localId of deleteLocalIds) {
                store.delete(chatKeyForOwner(owner.key, localId));
            }
            store.put({
                chatId: chatKeyForOwner(owner.key, put),
                tenantId: owner.key,
                localId: String(put),
                // Deliberately 1, NOT the current serializer version:
                // the row exists precisely to pass old builds' filters.
                schemaVersion: 1,
                idFence: true,
                kind: null,
                title: "New chat",
                hasItems: false,
                busyAtClose: false,
                savedAt: Date.now(),
            });
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => {
                console.warn("[chatStorage] ensureIdFence failed:", tx.error);
                resolve(false);
            };
            tx.onabort = () => {
                console.warn("[chatStorage] ensureIdFence aborted:", tx.error);
                resolve(false);
            };
        } catch (err) {
            console.warn("[chatStorage] ensureIdFence threw:", err);
            resolve(false);
        }
    });
}

// Returns the lightweight tab-strip metadata for every persisted chat
// of one tenant — the cheap read that backs the chat tab list on
// mount. Each record carries `localId` (remapped to `chatId` for the
// caller, mirroring loadAllChats) plus title / hasItems / busyAtClose
// / schemaVersion. Bodies are loaded lazily via loadChat on first
// activation.
//
// Empty result means EITHER the tenant has no chats OR a pre-v3 user
// hasn't had their bodies projected into `chatMeta` yet; the caller
// disambiguates by falling back to loadAllChats once and back-filling
// (see backfillChatMeta).
export async function loadAllChatMeta(tenantId) {
    const context = await getDbContext(tenantId);
    if (!context) return [];
    const { db, owner } = context;
    return new Promise((resolve) => {
        try {
            const tx = db.transaction("chatMeta", "readonly");
            const idx = tx.objectStore("chatMeta").index("byTenant");
            const req = idx.getAll(IDBKeyRange.only(owner.key));
            req.onsuccess = () => {
                const rows = Array.isArray(req.result) ? req.result : [];
                resolve(rows.map(remapForHydrate));
            };
            req.onerror = () => {
                console.warn("[chatStorage] loadAllChatMeta failed:", req.error);
                resolve([]);
            };
        } catch (err) {
            console.warn("[chatStorage] loadAllChatMeta threw:", err);
            resolve([]);
        }
    });
}

// One-shot migration helper: project tab-strip metadata for a batch of
// full chat payloads into the `chatMeta` store. Written in a SINGLE
// transaction so the back-fill is atomic — a partial failure leaves
// `chatMeta` empty and the caller retries on the next load, rather
// than leaving some chats indexed and others invisible. `payloads` is
// an array of { chatId (local id), payload } pairs.
export async function backfillChatMeta(tenantId, payloads) {
    if (_writesSuppressed) return false;
    const context = await getDbContext(tenantId);
    if (!context) return false;
    const { db, owner } = context;
    if (!Array.isArray(payloads) || payloads.length === 0) return true;
    return new Promise((resolve) => {
        try {
            const tx = db.transaction("chatMeta", "readwrite");
            const store = tx.objectStore("chatMeta");
            for (const { chatId, payload } of payloads) {
                if (chatId == null || !payload) continue;
                store.put(projectChatMetaForOwner(owner.key, chatId, payload));
            }
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => {
                console.warn("[chatStorage] backfillChatMeta failed:", tx.error);
                resolve(false);
            };
            tx.onabort = () => {
                console.warn("[chatStorage] backfillChatMeta aborted:", tx.error);
                resolve(false);
            };
        } catch (err) {
            console.warn("[chatStorage] backfillChatMeta threw:", err);
            resolve(false);
        }
    });
}

// Expose the local id as `chatId` for the hydrator, which doesn't know
// about tenant-composite keys. Falls back to the stored `chatId` for
// any record that predates `localId` (shouldn't happen post-migration).
function remapForHydrate(record) {
    if (!record) return record;
    return { ...record, chatId: record.localId ?? record.chatId };
}

// Persist the current snapshot of large tool-result rows for one
// chat. Replaces any prior entries — the caller passes the full
// current snapshot from toolResultStore.snapshotForPersistence().
// Each entry is { id (toolCallId), entry: { rows, source,
// recordedAt, sizeBytes, ... } }.
export async function saveLargeRows(tenantId, chatId, entries) {
    if (_writesSuppressed) return false;
    const context = await getDbContext(tenantId);
    if (!context) return false;
    const { db, owner } = context;
    const composite = chatKeyForOwner(owner.key, chatId);
    return new Promise((resolve) => {
        try {
            const tx = db.transaction("largeRows", "readwrite");
            const store = tx.objectStore("largeRows");
            const idx = store.index("chatId");
            // Clear existing entries for this chat first, then
            // insert the fresh snapshot — same transaction so a
            // mid-write failure leaves no orphans / partial state.
            const cursorReq = idx.openCursor(IDBKeyRange.only(composite));
            cursorReq.onsuccess = () => {
                const cursor = cursorReq.result;
                if (cursor) {
                    cursor.delete();
                    cursor.continue();
                } else {
                    for (const item of entries || []) {
                        if (!item?.id || !item.entry) continue;
                        store.put({
                            key: `${composite}:${item.id}`,
                            chatId: composite,
                            tenantId: owner.key,
                            toolCallId: item.id,
                            entry: item.entry,
                        });
                    }
                }
            };
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => {
                console.warn("[chatStorage] saveLargeRows failed:", tx.error);
                resolve(false);
            };
            tx.onabort = () => {
                console.warn("[chatStorage] saveLargeRows aborted:", tx.error);
                resolve(false);
            };
        } catch (err) {
            console.warn("[chatStorage] saveLargeRows threw:", err);
            resolve(false);
        }
    });
}

// Load every large-row entry persisted for a chat. Returned shape
// matches what toolResultStore.loadStore expects:
//   [{ id: toolCallId, entry: { rows, source, recordedAt, ... } }]
export async function loadLargeRows(tenantId, chatId) {
    const context = await getDbContext(tenantId);
    if (!context) return [];
    const { db, owner } = context;
    const composite = chatKeyForOwner(owner.key, chatId);
    return new Promise((resolve) => {
        try {
            const tx = db.transaction("largeRows", "readonly");
            const idx = tx.objectStore("largeRows").index("chatId");
            const req = idx.getAll(IDBKeyRange.only(composite));
            req.onsuccess = () => {
                const rows = Array.isArray(req.result) ? req.result : [];
                resolve(rows.map((r) => ({ id: r.toolCallId, entry: r.entry })));
            };
            req.onerror = () => {
                console.warn("[chatStorage] loadLargeRows failed:", req.error);
                resolve([]);
            };
        } catch (err) {
            console.warn("[chatStorage] loadLargeRows threw:", err);
            resolve([]);
        }
    });
}

// Drop a chat's persisted state, including any largeRows attached to
// it. Both stores are written in one transaction so a failure leaves
// no orphans.
export async function deleteChat(tenantId, chatId) {
    const context = await getDbContext(tenantId);
    if (!context) return false;
    const { db, owner } = context;
    const composite = chatKeyForOwner(owner.key, chatId);
    return new Promise((resolve) => {
        try {
            const tx = db.transaction(["chats", "chatMeta", "largeRows", "chatSync"], "readwrite");
            tx.objectStore("chats").delete(composite);
            tx.objectStore("chatMeta").delete(composite);
            tx.objectStore("chatSync").delete(composite);
            const rows = tx.objectStore("largeRows");
            const idx = rows.index("chatId");
            const cursorReq = idx.openCursor(IDBKeyRange.only(composite));
            cursorReq.onsuccess = () => {
                const cursor = cursorReq.result;
                if (cursor) {
                    cursor.delete();
                    cursor.continue();
                }
            };
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => {
                console.warn("[chatStorage] deleteChat failed:", tx.error);
                resolve(false);
            };
            tx.onabort = () => {
                console.warn("[chatStorage] deleteChat aborted:", tx.error);
                resolve(false);
            };
        } catch (err) {
            console.warn("[chatStorage] deleteChat threw:", err);
            resolve(false);
        }
    });
}

// Drop EVERY chat (and attached largeRows) for one tenant in a single
// transaction. Cursor-walks the `byTenant` index on both stores so
// other tenants on the same browser are untouched. Used by the
// "Clear chat history" flows in LogoutDialog and SettingsPage.
// Fires subscribeChatsCleared listeners on completion so ChatConsole
// can reset its in-memory chat state.
export async function deleteAllChatsForTenant(tenantId) {
    const context = await getDbContext(tenantId);
    if (!context) { notifyCleared(); return false; }
    const { db, owner } = context;
    const tk = owner.key;
    const ok = await new Promise((resolve) => {
        try {
            const tx = db.transaction(["chats", "chatMeta", "largeRows", "chatSync"], "readwrite");
            for (const storeName of ["chats", "chatMeta", "largeRows", "chatSync"]) {
                const idx = tx.objectStore(storeName).index("byTenant");
                const cursorReq = idx.openCursor(IDBKeyRange.only(tk));
                cursorReq.onsuccess = () => {
                    const cursor = cursorReq.result;
                    if (!cursor) return;
                    cursor.delete();
                    cursor.continue();
                };
            }
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => {
                console.warn("[chatStorage] deleteAllChatsForTenant failed:", tx.error);
                resolve(false);
            };
            tx.onabort = () => {
                console.warn("[chatStorage] deleteAllChatsForTenant aborted:", tx.error);
                resolve(false);
            };
        } catch (err) {
            console.warn("[chatStorage] deleteAllChatsForTenant threw:", err);
            resolve(false);
        }
    });
    notifyCleared();
    return ok;
}

// One-time deletion of pre-v2 (un-tenanted) chats.
//
// Records written before tenant scoping carry a bare integer `chatId`
// and no `tenantId`/`localId`. Rather than guess which tenant they
// belong to (a browser may have seen more than one), we drop them
// outright — clean isolation, no cross-tenant attribution. New chats
// start tenant-scoped from here on. Both stores are cleared in a
// single transaction so a failure leaves no partial state. A global
// flag prevents re-scanning on later mounts; once it's set the legacy
// rows are gone anyway, so a missed flag is merely a wasted no-op
// scan, not a correctness issue.
export async function purgeLegacyChats() {
    try {
        if (localStorage.getItem(PURGED_FLAG)) return false;
    } catch { /* localStorage unavailable — fall through and scan */ }

    if (typeof indexedDB === "undefined") return false;
    // The former database was tenant-only, so no row can be attributed to a
    // user safely. Delete the whole database instead of copying its contents.
    const done = await new Promise((resolve) => {
        const request = indexedDB.deleteDatabase(DB_NAME);
        request.onsuccess = () => resolve(true);
        request.onerror = () => resolve(false);
        request.onblocked = () => resolve(false);
    });

    if (done) {
        try { localStorage.setItem(PURGED_FLAG, "1"); } catch { /* noop */ }
    }
    return done;
}

// ── Pending-sync outbox (chat persistence) ─────────────────────────────
//
// One row per chat, keyed by the same tenant-composite id as `chats`, holding
// the local view of sync state (server-acked revision, dirty flag, terminal /
// conflict markers). The chat-sync engine owns the semantics; these are the
// thin IDB accessors. All fail soft like the rest of this module.

// Load every outbox row for a tenant. `localId` is remapped onto the caller's
// numeric id, mirroring loadAllChats.
export async function loadOutbox(tenantId) {
    const context = await getDbContext(tenantId);
    if (!context) return [];
    const { db, owner } = context;
    return new Promise((resolve) => {
        try {
            const tx = db.transaction("chatSync", "readonly");
            const idx = tx.objectStore("chatSync").index("byTenant");
            const req = idx.getAll(IDBKeyRange.only(owner.key));
            req.onsuccess = () => {
                const rows = Array.isArray(req.result) ? req.result : [];
                resolve(rows.map(remapForHydrate));
            };
            req.onerror = () => {
                console.warn("[chatStorage] loadOutbox failed:", req.error);
                resolve([]);
            };
        } catch (err) {
            console.warn("[chatStorage] loadOutbox threw:", err);
            resolve([]);
        }
    });
}

export async function getOutboxEntry(tenantId, chatId) {
    const context = await getDbContext(tenantId);
    if (!context) return null;
    const { db, owner } = context;
    const key = chatKeyForOwner(owner.key, chatId);
    return new Promise((resolve) => {
        try {
            const tx = db.transaction("chatSync", "readonly");
            const req = tx.objectStore("chatSync").get(key);
            req.onsuccess = () => resolve(remapForHydrate(req.result) || null);
            req.onerror = () => {
                console.warn("[chatStorage] getOutboxEntry failed:", req.error);
                resolve(null);
            };
        } catch (err) {
            console.warn("[chatStorage] getOutboxEntry threw:", err);
            resolve(null);
        }
    });
}

// Upsert one outbox row. `entry` carries the sync bookkeeping (serverChatId,
// ackedRevision, dirty, terminal, conflict, lastError, updatedAt).
export async function putOutboxEntry(tenantId, chatId, entry) {
    if (_writesSuppressed) return false;
    const context = await getDbContext(tenantId);
    if (!context) return false;
    const { db, owner } = context;
    const key = chatKeyForOwner(owner.key, chatId);
    return new Promise((resolve) => {
        try {
            const tx = db.transaction("chatSync", "readwrite");
            tx.objectStore("chatSync").put({
                ...entry,
                chatId: key,
                tenantId: owner.key,
                localId: String(chatId),
            });
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => {
                console.warn("[chatStorage] putOutboxEntry failed:", tx.error);
                resolve(false);
            };
            tx.onabort = () => {
                console.warn("[chatStorage] putOutboxEntry aborted:", tx.error);
                resolve(false);
            };
        } catch (err) {
            console.warn("[chatStorage] putOutboxEntry threw:", err);
            resolve(false);
        }
    });
}

// Read-modify-write ONE outbox row inside a single transaction.
//
// The get-then-put pair every other caller uses is fine when the caller owns
// the whole row, and wrong when two writers each contribute a PART of it: both
// read the pre-merge row and the second put drops the first's contribution.
// That is a real interleaving for the blob-dedup cache, which two restores of
// the same chat (two tabs, or a re-open during hydration) can both add to.
// IndexedDB serializes readwrite transactions over a store across connections,
// so doing both halves in one transaction makes the merge atomic between tabs
// too.
//
// `merge(entry)` receives the stored row (null when absent) and returns the row
// to store, or null to leave it untouched. It MUST be synchronous: awaiting
// inside a transaction lets it auto-commit underneath you, which would silently
// restore the race this exists to remove.
export async function mergeOutboxEntry(tenantId, chatId, merge) {
    if (_writesSuppressed) return false;
    const db = await getDb();
    if (!db) return false;
    const key = chatKey(tenantId, chatId);
    return new Promise((resolve) => {
        try {
            const tx = db.transaction("chatSync", "readwrite");
            const store = tx.objectStore("chatSync");
            const req = store.get(key);
            let merged = false;
            req.onsuccess = () => {
                const next = merge(remapForHydrate(req.result) || null);
                if (!next) return;
                store.put({
                    ...next,
                    chatId: key,
                    tenantId: tenantKey(tenantId),
                    localId: String(chatId),
                });
                merged = true;
            };
            tx.oncomplete = () => resolve(merged);
            tx.onerror = () => {
                console.warn("[chatStorage] mergeOutboxEntry failed:", tx.error);
                resolve(false);
            };
            tx.onabort = () => {
                console.warn("[chatStorage] mergeOutboxEntry aborted:", tx.error);
                resolve(false);
            };
        } catch (err) {
            console.warn("[chatStorage] mergeOutboxEntry threw:", err);
            resolve(false);
        }
    });
}

export async function deleteOutboxEntry(tenantId, chatId) {
    const context = await getDbContext(tenantId);
    if (!context) return false;
    const { db, owner } = context;
    const key = chatKeyForOwner(owner.key, chatId);
    return new Promise((resolve) => {
        try {
            const tx = db.transaction("chatSync", "readwrite");
            tx.objectStore("chatSync").delete(key);
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => resolve(false);
            tx.onabort = () => resolve(false);
        } catch (err) {
            console.warn("[chatStorage] deleteOutboxEntry threw:", err);
            resolve(false);
        }
    });
}
